import { useCallback, useEffect, useRef, useState } from "react";
import { UserProfilePrefs } from "../../../modules/Helpers/UserProfilePrefs";
import {
  fetchMyNotifications,
  fetchAllNotifications,
} from "../../apicalls/notificationFunctions";
import {
  filterNotificationsForBell,
  getWelcomeNotificationsForPopup,
} from "../../utils/notificationFilters";
import { toAppNotification } from "../../models/Notification/notificationTypes";
import type {
  AppNotification,
  NotificationCategory,
  RawNotification,
} from "../../models/Notification/notificationTypes";

function waitForLastLogin(
  cancelledRef: { readonly current: boolean },
  timeoutMs = 5000,
): Promise<string | null> {
  return new Promise((resolve) => {
    const prefs = UserProfilePrefs.getInstance() as {
      getLastLogin: () => string | null | undefined;
      getProfileAPICallStatus: () => string;
    };

    const safeResolve = (value: string | null) => {
      if (!cancelledRef.current) resolve(value);
    };

    const check = () => {
      const status = prefs.getProfileAPICallStatus();
      if (status === "COMPLETED" || status === "FAILED") {
        safeResolve(prefs.getLastLogin() ?? null);
        return true;
      }
      return false;
    };

    if (check()) return;

    const interval = setInterval(() => {
      if (check()) clearInterval(interval);
    }, 250);

    setTimeout(() => {
      clearInterval(interval);
      safeResolve(prefs.getLastLogin() ?? null);
    }, timeoutMs);
  });
}

const sortByPublishAtDesc = (a: AppNotification, b: AppNotification) =>
  b.publishAt.getTime() - a.publishAt.getTime();

export type NotificationFetchMode = "user" | "admin";

type UseNotificationsOptions = {
  mode: NotificationFetchMode;
  category: NotificationCategory;
};

export type UseNotificationsResult = {
  data: AppNotification[];
  popupNotifications: AppNotification[];
  isLoading: boolean;
  error: Error | null;
  reload: () => void;
};

export function useNotifications(
  options: UseNotificationsOptions,
): UseNotificationsResult {
  const { mode, category } = options;
  const [data, setData] = useState<AppNotification[]>([]);
  const [popupNotifications, setPopupNotifications] = useState<
    AppNotification[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reloadCounter = useRef(0);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => {
    reloadCounter.current += 1;
    setReloadTick(reloadCounter.current);
  }, []);

  useEffect(() => {
    const cancelledRef = { current: false };

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result: AppNotification[];

        if (mode === "user") {
          const raw = await fetchMyNotifications(category);
          if (cancelledRef.current) return;

          const all = raw.map(toAppNotification);
          const lastLoginRaw = await waitForLastLogin(cancelledRef);
          if (cancelledRef.current) return;

          const lastLogin =
            lastLoginRaw != null ? new Date(lastLoginRaw) : null;
          result = filterNotificationsForBell(all, lastLogin);

          const popup = getWelcomeNotificationsForPopup(all);
          if (!cancelledRef.current) {
            setPopupNotifications(popup);
          }
        } else {
          const raws: RawNotification[] = await fetchAllNotifications();
          if (cancelledRef.current) return;

          result = raws
            .filter((r) => r.notificationType === category)
            .map(toAppNotification)
            .sort(sortByPublishAtDesc);
        }

        setData(result);
      } catch (err) {
        if (cancelledRef.current) return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!cancelledRef.current) setIsLoading(false);
      }
    };

    load().catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error("useNotifications: unexpected error", err);
    });

    return () => {
      cancelledRef.current = true;
    };
  }, [mode, category, reloadTick]);

  return { data, popupNotifications, isLoading, error, reload };
}
